import {CardElement, Elements, useElements, useStripe} from '@stripe/react-stripe-js';
import type {CanMakePaymentResult, PaymentRequest, PaymentRequestPaymentMethodEvent, Stripe} from '@stripe/stripe-js';
import {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Analytics from 'utils/Analytics';
import validateEmail from 'utils/validateEmail';
import {createStripeGlp1Instance} from 'externalAPIs/stripe';
import {createWebAccount, createGlp1Setup, finalizeGlp1, type Glp1Variant} from 'externalAPIs/hypnos';
import {readSeverityFromUrl} from 'surveys/osa_funnel/survey/severity';
import {useGlp1Modality} from 'surveys/osa_funnel/hooks/useGlp1Modality';
import {modalityToVariant} from './paywallCopy';
import styles from './Glp1PaymentBlock.module.css';

const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `glp1-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const PRICE_USD = 69;
const CART_LABEL = 'RISE Care Membership';

/** Heuristic: did web-create-account fail because the email is already
 *  registered to a RISE account (vs. a network / server error)? The
 *  backend surfaces a message string; match the "already exists / in use"
 *  family so we only emit the existing-email telemetry for that case. */
const isEmailInUseError = (message?: string): boolean =>
  !!message && /already|exist|in[\s-]?use|taken|registered|associated|duplicate/i.test(message);

type Glp1PaymentBlockProps = {
  ctaLabel: string;
  /** Fired whenever the inline CTA's enabled state changes — wallet
   *  ready, or email+card valid. The parent paywall uses this to keep
   *  the sticky bottom CTA in sync. */
  onReadyChange?: (ready: boolean) => void;
};

/** Imperative handle so the paywall's sticky CTA can fire the same
 *  action as the inline button (Apple Pay sheet OR card submit). */
export type Glp1PaymentBlockHandle = {
  submit: () => void;
};

/** Outer wrapper — loads Stripe SDK once, then renders the inner block
 *  inside <Elements>. We DON'T pass a clientSecret at mount: the
 *  SetupIntent is created on-demand only after the user picks a payment
 *  method, so we don't burn a hypnos account + intent for every paywall
 *  view. */
export const Glp1PaymentBlock = forwardRef<Glp1PaymentBlockHandle, Glp1PaymentBlockProps>(
  ({ctaLabel, onReadyChange}, ref) => {
    const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);

    useEffect(() => {
      let cancelled = false;
      createStripeGlp1Instance().then(stripe => {
        if (!cancelled) setStripeInstance(stripe ?? null);
      });
      return () => {
        cancelled = true;
      };
    }, []);

    if (!stripeInstance) {
      return (
        <div className={styles.loading}>
          <span className={styles.loadingSpinner} />
          Loading payment options…
        </div>
      );
    }

    return (
      <Elements stripe={stripeInstance}>
        <InnerPaymentBlock ref={ref} ctaLabel={ctaLabel} onReadyChange={onReadyChange} />
      </Elements>
    );
  },
);
Glp1PaymentBlock.displayName = 'Glp1PaymentBlock';

type Phase =
  | {kind: 'idle'}
  | {kind: 'processing_applepay'}
  | {kind: 'processing_card'}
  | {kind: 'succeeded'}
  // Generic retry-able failure (network, declined card, etc.)
  | {kind: 'error'; message: string}
  // Charge cleared at Stripe but a downstream hypnos step failed.
  // Per the Hypnos spec, do NOT retry — that would re-charge the user.
  // Surface a support-contact message + lock the submit button.
  | {kind: 'charged_but_failed'; message: string};

/** Renders the actual payment UI inside <Elements>.
 *
 *  Default path (Apple Pay / Google Pay / Link available):
 *    - PaymentRequestButtonElement is the primary CTA. Wallet sheet
 *      collects the email; we use it to create a hypnos account and
 *      lazily mint the SetupIntent at confirm time.
 *    - "Or use a credit card" link reveals the manual card form.
 *
 *  Fallback (no wallet support):
 *    - Email input + CardElement shown by default. */
const InnerPaymentBlock = forwardRef<Glp1PaymentBlockHandle, Glp1PaymentBlockProps>(
  ({ctaLabel, onReadyChange}, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const modality = useGlp1Modality();
    const variant = modalityToVariant(modality);

    const [phase, setPhase] = useState<Phase>({kind: 'idle'});
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
    const [canMakePayment, setCanMakePayment] = useState<CanMakePaymentResult | null>(null);
    const [walletChecked, setWalletChecked] = useState(false);
    const [showCardForm, setShowCardForm] = useState(false);

    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);

    const idempotencyRef = useRef<string | null>(null);

    // ─── Build the PaymentRequest on mount + probe Apple Pay support ──
    useEffect(() => {
      if (!stripe) return;
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {label: CART_LABEL, amount: PRICE_USD * 100},
        requestPayerName: true,
        requestPayerEmail: true,
      });
      pr.canMakePayment().then(result => {
        setWalletChecked(true);
        if (result) {
          setPaymentRequest(pr);
          setCanMakePayment(result);
        }
      });
    }, [stripe]);

    /** Shared: account creation → SetupIntent → confirm → finalize. */
    const runPurchase = useCallback(
      async (params: {
        payerEmail: string;
        confirm: (clientSecret: string) => Promise<{error?: {message?: string}; ok: boolean}>;
        onApplePayComplete?: (status: 'success' | 'fail') => void;
      }) => {
        const {payerEmail, confirm, onApplePayComplete} = params;
        if (!stripe) return;

        // 1. Create the account WITH the payer's email FIRST, via the web
        // create-account endpoint — BEFORE any charge. If that email already
        // belongs to a RISE account (or creation otherwise fails), we stop
        // here: nothing is charged and the user can't move forward. This is
        // the fix for the old order, where an anonymous account was created
        // and charged first, then the email was bound afterward — so a
        // duplicate email failed AFTER the charge, leaving the user paid but
        // on an orphaned account.
        const accountResult = await createWebAccount(payerEmail);
        if (accountResult.error) {
          const method = onApplePayComplete ? 'apple_pay' : 'card';
          const emailInUse = isEmailInUseError(accountResult.errorMessage);
          if (emailInUse) {
            // The common Apple Pay case: the wallet auto-fills an email that
            // already has a RISE account. Track it so we can size the impact.
            Analytics.track('Apple Pay Purchase Attempted with Existing Email', {
              variant,
              severity: readSeverityFromUrl(),
              method,
            });
            // Let them recover: reveal the card form (with its email field)
            // so they can enter a DIFFERENT email and continue. An Apple Pay
            // user can't change the wallet-shared email inline, so this is
            // their path forward.
            setShowCardForm(true);
          }
          onApplePayComplete?.('fail');
          setPhase({
            kind: 'error',
            message: emailInUse
              ? 'That email already has a RISE account, so we didn’t charge you. Enter a different email to continue.'
              : accountResult.errorMessage || 'Could not start checkout.',
          });
          return;
        }
        const {person_id, token} = accountResult;

        // 2. Mint the SetupIntent for this attempt.
        const setupResult = await createGlp1Setup(variant, person_id, token);
        if (!setupResult.ok) {
          onApplePayComplete?.('fail');
          setPhase({kind: 'error', message: setupResult.errorMessage || 'Could not start checkout.'});
          return;
        }
        const {client_secret, setup_intent_id} = setupResult.data;

        // 3. Confirm the SetupIntent — the caller knows whether it's an
        // Apple Pay paymentMethod id or the card element + billing.
        const {error: confirmError, ok} = await confirm(client_secret);
        if (!ok || confirmError) {
          onApplePayComplete?.('fail');
          setPhase({kind: 'error', message: confirmError?.message ?? 'Could not authorize payment.'});
          return;
        }
        onApplePayComplete?.('success');

        // 4. Finalize off-session at hypnos — THE CHARGE. Only reached
        // after the account exists (step 1), so we never charge a user we
        // couldn't create. Same body shape as Nyx-iOS — person_id required.
        if (!idempotencyRef.current) idempotencyRef.current = newIdempotencyKey();
        const finalizeResult = await finalizeGlp1({
          setupIntentId: setup_intent_id,
          email: payerEmail,
          idempotencyKey: idempotencyRef.current,
          personId: person_id,
          token,
        });
        if (!finalizeResult.ok) {
          // If the charge cleared at Stripe but hypnos failed downstream
          // (status 500 with an error body), retrying would charge the
          // user a second time. Lock the form and tell them to contact
          // support. Per iOS spec.
          if (finalizeResult.chargeMayHaveSucceeded) {
            setPhase({
              kind: 'charged_but_failed',
              message:
                "Your card was charged, but we couldn't finish setting up your care. Please contact support@risescience.com — we'll refund your card and help you get started.",
            });
            return;
          }
          setPhase({kind: 'error', message: finalizeResult.errorMessage || 'Charge failed. Try again.'});
          return;
        }
        idempotencyRef.current = null;

        // The email was bound at account creation (step 1), so there's no
        // post-charge email step — we never call /api/update-email.
        Analytics.track('GLP-1 Paywall Purchased', {
          variant,
          method: onApplePayComplete ? 'apple_pay' : 'card',
          severity: readSeverityFromUrl(),
        });
        setPhase({kind: 'succeeded'});
        // Carry the query string (returnTo, modality, severity) into the
        // screener so its exit redirect honours ?returnTo=.
        navigate(`/osa/screening${window.location.search}`);
      },
      [stripe, variant, navigate],
    );

    // ─── Apple Pay paymentmethod handler ──────────────────────────────
    useEffect(() => {
      if (!paymentRequest || !stripe) return;
      const handler = async (event: PaymentRequestPaymentMethodEvent) => {
        const payerEmail = (event.payerEmail ?? '').trim();
        if (!payerEmail) {
          event.complete('fail');
          setPhase({kind: 'error', message: 'Apple Pay didn’t share an email. Try the card path.'});
          return;
        }
        setPhase({kind: 'processing_applepay'});
        await runPurchase({
          payerEmail,
          confirm: async clientSecret => {
            const {error, setupIntent} = await stripe.confirmCardSetup(
              clientSecret,
              {payment_method: event.paymentMethod.id},
              {handleActions: false},
            );
            if (error) return {ok: false, error: {message: error.message}};
            if (setupIntent && setupIntent.status === 'requires_action') {
              const {error: actionError} = await stripe.confirmCardSetup(clientSecret);
              if (actionError) return {ok: false, error: {message: actionError.message}};
            }
            return {ok: true};
          },
          onApplePayComplete: status => event.complete(status),
        });
      };
      paymentRequest.on('paymentmethod', handler);
      return () => {
        paymentRequest.off('paymentmethod', handler);
      };
    }, [paymentRequest, stripe, runPurchase]);

    const trimmedEmail = email.trim();
    const emailValid = validateEmail(trimmedEmail);
    const showEmailError = emailTouched && !emailValid && trimmedEmail.length > 0;

    // Wallet CTA — track the tap, then open the wallet sheet. Shared by
    // the inline "Start my care" button AND the sticky-bottom CTA's
    // imperative submit, so the event fires from either entry point.
    // paymentRequest.show() stays a synchronous call off the click so it
    // still counts as a user gesture.
    const handleWalletClick = useCallback(() => {
      Analytics.track('GLP-1 Paywall Apple Pay Tapped', {variant, severity: readSeverityFromUrl()});
      paymentRequest?.show();
    }, [variant, paymentRequest]);

    // "Or use a credit card" — reveal the manual card form.
    const handleExpandCardForm = () => {
      Analytics.track('GLP-1 Paywall Card Form Expanded', {variant, severity: readSeverityFromUrl()});
      setShowCardForm(true);
    };

    // Card form submit.
    const handleCardSubmit = async () => {
      // Fires on every submit tap (inline button or sticky CTA), before
      // validation — captures intent even if email/card aren't yet valid.
      Analytics.track('GLP-1 Paywall Card Submit Tapped', {variant, severity: readSeverityFromUrl()});
      if (!stripe || !elements) return;
      if (!emailValid) {
        setEmailTouched(true);
        return;
      }
      if (!cardComplete) return;
      const card = elements.getElement(CardElement);
      if (!card) return;

      setPhase({kind: 'processing_card'});
      await runPurchase({
        payerEmail: trimmedEmail,
        confirm: async clientSecret => {
          const {error} = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {card, billing_details: {email: trimmedEmail}},
          });
          if (error) return {ok: false, error: {message: error.message}};
          return {ok: true};
        },
      });
    };

    const busy = phase.kind === 'processing_applepay' || phase.kind === 'processing_card';
    // Lock all controls when the charge cleared but a downstream step
    // failed — retrying would double-charge the user. They need to
    // contact support; the banner above explains why the buttons are
    // disabled.
    const locked = phase.kind === 'charged_but_failed' || phase.kind === 'succeeded';
    const walletAvailable = !!(paymentRequest && canMakePayment);
    // Card form auto-renders if (a) wallet probe finished and there's no
    // wallet support, or (b) user explicitly expanded the card form.
    const cardFormVisible = (walletChecked && !walletAvailable) || showCardForm;

    // Surface the "user can actually pay right now" state up to the
    // paywall shell so its sticky-bottom CTA can mirror the inline
    // button's disabled state. Wallet button is always "ready" once
    // we've confirmed wallet support; the card form is ready once the
    // email + card are valid.
    const inlineReady = !busy && !locked && (walletAvailable && !cardFormVisible ? true : emailValid && cardComplete);
    const lastReadyRef = useRef<boolean | null>(null);
    useEffect(() => {
      if (lastReadyRef.current === inlineReady) return;
      lastReadyRef.current = inlineReady;
      onReadyChange?.(inlineReady);
    }, [inlineReady, onReadyChange]);

    // Expose `submit()` so the parent's sticky CTA fires the same
    // action as the inline button — wallet sheet for the Apple Pay path
    // OR the card submit otherwise. paymentRequest.show() still counts as
    // a user gesture when invoked synchronously from the sticky button's
    // click handler.
    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (busy) return;
          if (walletAvailable && !cardFormVisible) {
            handleWalletClick();
            return;
          }
          handleCardSubmit();
        },
      }),
      // handleCardSubmit captures stripe/elements/email/card state via its
      // closure; safe to recreate the imperative handle whenever those
      // change.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [busy, walletAvailable, cardFormVisible, paymentRequest, stripe, elements, trimmedEmail, cardComplete],
    );

    return (
      <div className={styles.root}>
        {phase.kind === 'error' ? <div className={styles.errorBanner}>{phase.message}</div> : null}
        {phase.kind === 'charged_but_failed' ? <div className={styles.errorBanner}>{phase.message}</div> : null}

        {/* Wallet path: a "Start my care" button (NOT the native Apple Pay
          button) opens the wallet sheet. paymentRequest.show() is called
          synchronously from this click so it counts as a user gesture.
          Hidden once the user switches to the card form, and when locked
          (prevents a re-charge after a charged_but_failed state). */}
        {walletAvailable && !locked && !cardFormVisible ? (
          <>
            <button type="button" className={styles.submit} onClick={handleWalletClick} disabled={busy}>
              {phase.kind === 'processing_applepay' ? (
                <span className={styles.loading}>
                  <span className={styles.loadingSpinner} />
                  Authorizing…
                </span>
              ) : (
                ctaLabel
              )}
            </button>
            <button type="button" className={styles.expandLink} onClick={handleExpandCardForm} disabled={busy}>
              Or use a credit card
            </button>
          </>
        ) : null}

        {/* Card form — shown when no wallet OR the user expanded it. */}
        {cardFormVisible ? (
          <>
            {walletAvailable ? (
              <>
                <div className={styles.divider}>
                  <span className={styles.dividerLine} />
                  <span>or pay with card</span>
                  <span className={styles.dividerLine} />
                </div>
                <button
                  type="button"
                  className={styles.expandLink}
                  onClick={() => setShowCardForm(false)}
                  disabled={busy}
                >
                  ← Use Apple Pay instead
                </button>
              </>
            ) : null}

            <div className={styles.emailField}>
              <label className={styles.emailLabel} htmlFor="osa-paywall-email">
                Email
              </label>
              <input
                id="osa-paywall-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                className={`${styles.emailInput} ${showEmailError ? styles.emailInputError : ''}`}
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                placeholder="you@example.com"
                disabled={busy}
              />
              {showEmailError ? <span className={styles.emailError}>Enter a valid email to continue.</span> : null}
            </div>

            <div className={styles.cardField}>
              <div className={`${styles.cardElementBox} ${cardError ? styles.cardElementBoxError : ''}`}>
                <CardElement
                  options={{hidePostalCode: false}}
                  onChange={e => {
                    setCardComplete(e.complete);
                    setCardError(e.error ? e.error.message : null);
                  }}
                />
              </div>
              {cardError ? <span className={styles.cardError}>{cardError}</span> : null}
            </div>

            <p className={styles.terms}>
              By tapping {ctaLabel.toLowerCase()}, you agree to RISE Expert Care&apos;s{' '}
              <a href="https://www.risescience.com/terms" target="_blank" rel="noopener noreferrer">
                Terms
              </a>{' '}
              and{' '}
              <a href="https://www.risescience.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </p>

            <button
              type="button"
              className={styles.submit}
              onClick={handleCardSubmit}
              disabled={!emailValid || !cardComplete || busy}
            >
              {phase.kind === 'processing_card' ? (
                <span className={styles.loading}>
                  <span className={styles.loadingSpinner} />
                  Authorizing…
                </span>
              ) : (
                ctaLabel
              )}
            </button>
          </>
        ) : null}

        {/* Pre-probe spinner so the buttons don't flash in. */}
        {!walletChecked ? (
          <div className={styles.loading}>
            <span className={styles.loadingSpinner} />
            Loading payment options…
          </div>
        ) : null}
      </div>
    );
  },
);
InnerPaymentBlock.displayName = 'InnerPaymentBlock';

export type {Glp1Variant};
